import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Building2, MapPin, Users, ChevronRight, Utensils, Bed, Ship, Gem, Store, Briefcase, HeartPulse, Car, Hammer, Sparkles, Star, Crown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  utensils: Utensils,
  bed: Bed,
  ship: Ship,
  gem: Gem,
  store: Store,
  briefcase: Briefcase,
  "heart-pulse": HeartPulse,
  car: Car,
  hammer: Hammer,
  sparkles: Sparkles,
};

export default function HomePage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [categories, setCategories] = useState([]);
  const [businessCount, setBusinessCount] = useState(0);
  const [featuredBusinesses, setFeaturedBusinesses] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCategories();
    fetchBusinessCount();
    fetchFeaturedBusinesses();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/categories`);
      setCategories(res.data);
    } catch (e) {
      console.error("Error fetching categories:", e);
    }
  };

  const fetchBusinessCount = async () => {
    try {
      const res = await axios.get(`${API}/businesses`);
      setBusinessCount(res.data.length);
    } catch (e) {
      console.error("Error fetching businesses:", e);
    }
  };

  const fetchFeaturedBusinesses = async () => {
    try {
      const res = await axios.get(`${API}/businesses/featured`);
      setFeaturedBusinesses(res.data);
    } catch (e) {
      console.error("Error fetching featured:", e);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/recherche?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleCategoryClick = (categoryId) => {
    navigate(`/recherche?category=${categoryId}`);
  };

  return (
    <div className="min-h-screen bg-sand">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-lagoon-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="home-logo">
            <div className="w-10 h-10 rounded-full bg-lagoon flex items-center justify-center">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <span className="font-serif text-xl font-semibold text-lagoon-900">Annuaire Polynésien</span>
          </Link>
          <nav className="flex items-center gap-4">
            <Link 
              to="/inscrire" 
              className="btn-coral text-sm py-2 px-6"
              data-testid="register-business-btn"
            >
              Inscrire mon entreprise
            </Link>
            <Link 
              to="/admin" 
              className="text-lagoon-600 hover:text-lagoon-900 font-medium text-sm"
              data-testid="admin-link"
            >
              Admin
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: "url('https://images.unsplash.com/photo-1649606680369-99b40c91b829?w=1920')",
          }}
        />
        <div className="absolute inset-0 hero-gradient" />
        
        <div className="relative max-w-7xl mx-auto px-6">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center text-white max-w-3xl mx-auto"
          >
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight tracking-tight">
              Découvrez les entreprises de Polynésie française
            </h1>
            <p className="text-lg md:text-xl text-white/90 mb-10">
              L'annuaire de référence pour trouver les patentes et entreprises polynésiennes
            </p>

            {/* Search Form */}
            <form onSubmit={handleSearch} className="max-w-2xl mx-auto" data-testid="search-form">
              <div className="flex flex-col sm:flex-row gap-3 bg-white/10 backdrop-blur-sm p-3 rounded-2xl">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-lagoon-600" />
                  <Input
                    type="text"
                    placeholder="Rechercher une entreprise, un service..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-12 h-14 bg-white border-0 rounded-xl text-lagoon-900 placeholder:text-gray-400 text-base"
                    data-testid="search-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="btn-coral h-14 px-8 text-base"
                  data-testid="search-submit-btn"
                >
                  Rechercher
                </Button>
              </div>
            </form>
          </motion.div>

          {/* Stats */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-wrap justify-center gap-8 mt-16"
          >
            <div className="flex items-center gap-3 text-white/90">
              <Building2 className="w-6 h-6" />
              <span className="text-lg"><strong>{businessCount}</strong> Entreprises</span>
            </div>
            <div className="flex items-center gap-3 text-white/90">
              <Users className="w-6 h-6" />
              <span className="text-lg"><strong>{categories.length}</strong> Catégories</span>
            </div>
            <div className="flex items-center gap-3 text-white/90">
              <MapPin className="w-6 h-6" />
              <span className="text-lg">Polynésie française</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Businesses Section */}
      {featuredBusinesses.length > 0 && (
        <section className="py-16 md:py-24 px-6 bg-gradient-to-b from-golden/10 to-sand">
          <div className="max-w-7xl mx-auto">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-golden/20 rounded-full mb-4">
                <Crown className="w-5 h-5 text-golden" />
                <span className="text-lagoon-900 font-semibold text-sm">Entreprises en vedette</span>
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-lagoon-900 mb-4">
                Nos partenaires privilégiés
              </h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                Découvrez les entreprises polynésiennes qui se démarquent par leur excellence
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredBusinesses.map((business, index) => (
                <motion.div
                  key={business.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link 
                    to={`/entreprise/${business.id}`}
                    className="block relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-golden/30 group"
                    data-testid={`featured-business-${business.id}`}
                  >
                    {/* Featured Badge */}
                    <div className="absolute top-4 right-4 z-10 flex items-center gap-1 px-3 py-1 bg-golden text-lagoon-900 rounded-full text-xs font-bold shadow-lg">
                      <Star className="w-3 h-3 fill-lagoon-900" />
                      En vedette
                    </div>
                    
                    <div className="p-6">
                      <div className="flex items-start gap-4 mb-4">
                        {business.logo_base64 ? (
                          <img 
                            src={business.logo_base64} 
                            alt={business.name}
                            className="w-20 h-20 rounded-xl object-cover ring-2 ring-golden/30"
                          />
                        ) : (
                          <div className="w-20 h-20 rounded-xl logo-placeholder text-2xl ring-2 ring-golden/30">
                            {business.name.charAt(0)}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-serif text-xl font-semibold text-lagoon-900 truncate group-hover:text-lagoon">
                            {business.name}
                          </h3>
                          <span className="inline-block px-2 py-0.5 bg-lagoon-50 text-lagoon-600 rounded text-xs mt-1">
                            {business.category_name}
                          </span>
                        </div>
                      </div>
                      
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                        {business.description}
                      </p>

                      <div className="flex items-center justify-between pt-4 border-t border-golden/20">
                        <span className="text-lagoon text-sm font-medium group-hover:underline">
                          Voir les détails
                        </span>
                        <ChevronRight className="w-4 h-4 text-lagoon group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Categories Section */}
      <section className="py-20 md:py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-lagoon-900 mb-4">
              Explorer par catégorie
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Trouvez facilement les entreprises qui correspondent à vos besoins
            </p>
          </motion.div>

          {/* Bento Grid Layout */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {categories.slice(0, 6).map((category, index) => {
              const IconComponent = iconMap[category.icon] || Building2;
              const isLarge = index === 0 || index === 3;
              
              return (
                <motion.div
                  key={category.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`${isLarge ? 'md:col-span-8' : 'md:col-span-4'} ${index === 0 ? 'md:row-span-2' : ''}`}
                >
                  <button
                    onClick={() => handleCategoryClick(category.id)}
                    className="relative w-full h-full min-h-[200px] md:min-h-[240px] rounded-2xl overflow-hidden group"
                    data-testid={`category-${category.id}`}
                  >
                    {category.image_url ? (
                      <img 
                        src={category.image_url} 
                        alt={category.name}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    ) : (
                      <div className="absolute inset-0 category-placeholder" />
                    )}
                    <div className="absolute inset-0 category-overlay" />
                    <div className="absolute bottom-0 left-0 right-0 p-6 text-left">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                          <IconComponent className="w-5 h-5 text-white" />
                        </div>
                      </div>
                      <h3 className="font-serif text-xl md:text-2xl font-semibold text-white mb-1">
                        {category.name}
                      </h3>
                      <p className="text-white/80 text-sm">{category.description}</p>
                    </div>
                    <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <ChevronRight className="w-5 h-5 text-white" />
                    </div>
                  </button>
                </motion.div>
              );
            })}
          </div>

          {/* Remaining Categories */}
          {categories.length > 6 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              {categories.slice(6).map((category, index) => {
                const IconComponent = iconMap[category.icon] || Building2;
                return (
                  <motion.button
                    key={category.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    onClick={() => handleCategoryClick(category.id)}
                    className="glass-card rounded-xl p-6 text-left hover:border-lagoon-200"
                    data-testid={`category-${category.id}`}
                  >
                    <div className="w-12 h-12 rounded-full bg-lagoon-50 flex items-center justify-center mb-4">
                      <IconComponent className="w-6 h-6 text-lagoon" />
                    </div>
                    <h3 className="font-semibold text-lagoon-900 mb-1">{category.name}</h3>
                    <p className="text-sm text-gray-500">{category.description}</p>
                  </motion.button>
                );
              })}
            </div>
          )}

          {/* View All Button */}
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Button
              onClick={() => navigate('/recherche')}
              className="btn-primary px-8"
              data-testid="view-all-btn"
            >
              Voir toutes les entreprises
            </Button>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 px-6 bg-lagoon-900">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Vous êtes une entreprise polynésienne ?
            </h2>
            <p className="text-lagoon-100 text-lg mb-10 max-w-2xl mx-auto">
              Inscrivez-vous gratuitement dans notre annuaire et gagnez en visibilité auprès des polynésiens et visiteurs.
            </p>
            <Button
              onClick={() => navigate('/inscrire')}
              className="btn-coral text-lg px-10 py-4"
              data-testid="cta-register-btn"
            >
              Inscrire mon entreprise gratuitement
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-white border-t border-lagoon-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-lagoon flex items-center justify-center">
                <MapPin className="w-4 h-4 text-white" />
              </div>
              <span className="font-serif text-lg font-semibold text-lagoon-900">Annuaire Polynésien</span>
            </div>
            <p className="text-gray-500 text-sm">
              © 2026 Annuaire Polynésien. Tous droits réservés.
            </p>
            <div className="flex items-center gap-6">
              <Link to="/admin" className="text-gray-500 hover:text-lagoon text-sm">Administration</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
